import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";

import { convertMsalAccountToUser, exchangeTokenWithBackend } from "../services/auth";
import { loginRequest as msalLoginRequest } from "../config/authConfig";
import { AuthContext } from "./AuthContext";

const normalizeAccessKey = (value) =>
  value
    ?.toString()
    .trim()
    .replace(/[\s_.-]+/g, " ")
    .toLowerCase();

const ROLE_MODULE_ACCESS = {
  admin: ["*"],
  difot: ["difot", "difot-report", "jupiter-portal"],
  user: ["dashboard", "report", "reporting", "jupiter-portal"],
  "customer solution": ["ticket-performance", "kpi-performance", "difot", "difot-report", "customer-health", "ratecard", "jupiter-portal"],
  "reporting hub": ["reporting"],
  "customer health": ["customer-health"],
  // Kept as named roles so access can be granted without the broad "user" role.
  "performance report": ["performance-report"],
  finance: ["reporting"],
};

// This map must stay in sync with ROLE_MODULE_ACCESS in
// athenaAPI/app/middleware/auth_dependency.py. Granting a module here without the
// backend shows the menu item but 403s every request; granting it there without
// here hides the menu from users who do have access.

const GROUP_MODULE_ACCESS = {
  "team-pricing": ["ratecard", "pricing-report"],
  "team-dc": ["data-cleaning", "difot"],
};

const buildAuthUser = ({ payload = {}, account = null }) => ({
  name: account?.name || payload?.name || payload?.preferred_username || "",
  email: payload?.upn || payload?.preferred_username || account?.username,
  id: payload?.oid || account?.localAccountId,
  roles: payload?.roles || [],
  groups: payload?.groups || [],
  permissions: payload?.permissions || [],
});

const persistAuthUser = (authUser) => {
  localStorage.setItem("authUser", JSON.stringify(authUser));
};

const clearStoredAuth = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("authUser");
};

const TOKEN_EXPIRY_BUFFER_SECONDS = 30;

function decodeJwt(token) {
  if (!token) return null;
  const payloadBase64 = token.split(".")[1];
  if (!payloadBase64) return null;
  try {
    const decoded = JSON.parse(atob(payloadBase64.replace(/-/g, "+").replace(/_/g, "/")));
    return decoded;
  } catch {
    return null;
  }
}

function isTokenExpired(token) {
  const payload = decodeJwt(token);
  if (!payload?.exp) return true;

  const expiresAtMs = (payload.exp - TOKEN_EXPIRY_BUFFER_SECONDS) * 1000;
  return expiresAtMs <= Date.now();
}

function getStoredToken() {
  const storedToken = localStorage.getItem("token");

  if (!storedToken) return null;
  if (!isTokenExpired(storedToken)) return storedToken;

  clearStoredAuth();
  return null;
}

export function AuthProvider({ children }) {
  const { instance, accounts, inProgress } = useMsal();

  const [user, setUser] = useState(() => {
    const cached = localStorage.getItem("authUser");
    return cached ? JSON.parse(cached) : null;
  });
  const [token, setToken] = useState(() => getStoredToken());
  const [isAuthLoading, setIsAuthLoading] = useState(() => accounts.length > 0);

  const resetAuthState = () => {
    setToken(null);
    setUser(null);
    clearStoredAuth();
  };

  useEffect(() => {
    if (inProgress === InteractionStatus.None && accounts.length > 0 && !user) {
      const account = accounts[0];
      const authUser = convertMsalAccountToUser(account);
      setUser(authUser);
      persistAuthUser(authUser);
    }
  }, [accounts, inProgress, user]);

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) {
      return;
    }

    if (accounts.length === 0) {
      setIsAuthLoading(false);
      return;
    }

    const account = accounts[0];
    setIsAuthLoading(true);

    instance
      .acquireTokenSilent({ ...msalLoginRequest, account })
      .then(async (response) => {
        const backendToken = await exchangeTokenWithBackend(response.accessToken);

        if (isTokenExpired(backendToken)) {
          throw new Error("Authentication token has expired");
        }

        setToken(backendToken);

        // Build user from idTokenClaims / access token claims
        const tokenClaims = response.idTokenClaims || decodeJwt(response.accessToken) || {};
        const authUser = buildAuthUser({ payload: tokenClaims, account });
        setUser(authUser);
        persistAuthUser(authUser);
      })
      .catch((error) => {
        console.error("Silent token acquisition failed:", error);
        resetAuthState();
        instance.logoutRedirect({
          account,
          postLogoutRedirectUri: `${window.location.origin}/auth/login`,
        });
      })
      .finally(() => {
        setIsAuthLoading(false);
      });
  }, [accounts, inProgress, instance]);

  useEffect(() => {
    if (token) {
      if (isTokenExpired(token)) {
        resetAuthState();
        return;
      }

      localStorage.setItem("token", token);
      const payload = decodeJwt(token);
      if (payload) {
        const updated = buildAuthUser({ payload });
        setUser(updated);
        persistAuthUser(updated);
      }
    } else {
      clearStoredAuth();
    }
  }, [token]);

  const hasRole = (requiredRoles = []) => {
    if (!user?.roles?.length) return false;
    return requiredRoles.some((r) => user.roles.includes(r));
  };

  const hasPermission = (moduleKey, action = "read") => {
    if (!user) return false;
    if (!moduleKey) return true;

    const perms = user.permissions || [];
    if (perms.includes(`${moduleKey}:${action}`) || perms.includes(`${moduleKey}:*`)) return true;

    const normalizedRoles = (user.roles || []).map(normalizeAccessKey);
    const hasRoleAccess = normalizedRoles.some((role) => {
      const allowed = ROLE_MODULE_ACCESS[role];
      return allowed?.includes("*") || allowed?.includes(moduleKey);
    });
    if (hasRoleAccess) return true;

    const normalizedGroups = (user.groups || []).map((g) => g?.toLowerCase());
    const hasGroupAccess = normalizedGroups.some((group) => {
      const allowed = GROUP_MODULE_ACCESS[group];
      return allowed?.includes("*") || allowed?.includes(moduleKey);
    });
    if (hasGroupAccess) return true;

    return false;
  };

  async function loginWithSSO() {
    try {
      await instance.loginRedirect(msalLoginRequest);
    } catch (error) {
      console.error("SSO login failed:", error);
      throw error;
    }
  }

  async function logout() {
    resetAuthState();

    if (accounts.length > 0) {
      await instance.logoutRedirect({
        account: accounts[0],
        postLogoutRedirectUri: `${window.location.origin}/auth/login`,
      });
      return;
    }

    window.location.replace("/auth/login");
  }

  const isAuthenticated = !!token && !isTokenExpired(token);
  const isAuthReady = inProgress === InteractionStatus.None && !isAuthLoading;

  return (
    <AuthContext.Provider
      value={{ user, token, loginWithSSO, logout, isAuthenticated, isAuthLoading, isAuthReady, hasRole, hasPermission }}
    >
      {children}
    </AuthContext.Provider>
  );
}
